import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { map, tap } from 'rxjs/operators';

import { LocationsActions, ContactsActions } from '@actions/index'
import * as fromLocations from '@reducers/index'
import { Observable, of } from 'rxjs';
import { LocationModel, RequestModel, FilterContactsModel, UpgradeAction, AiAutoReplyConfigModel, EmployeeResponse, EmployeeDTO, TaxonomyModel, InsightModel, LabelAggregationModel } from '@models/index';
import { FilterByqueryData, addRandomImageToLocation } from '@utilities/function-utils';
import { HttpClient } from '@angular/common/http';
import { environment } from '@environments/environment';

@Injectable({
  providedIn: 'root'
})
export class LocationsFacadeService {

  constructor(
    private store: Store<any>,
    private http: HttpClient
  ) { }

  searchAndNavigateToBusinessLocation(ids: { businessId: string, locationId: string }) {
    this.store.dispatch(LocationsActions.searchAndNavigateToBusinessLocation(ids));
  }

  getLocations$(): Observable<LocationModel[]> {
    return this.store
      .select(fromLocations.getLocations)
      .pipe(
        map(items => items.map(addRandomImageToLocation))
      )
  }

  getArchivedLocations$(): Observable<string[]> {
    return this.store.select(fromLocations.getArchivedLocations)
  }

  getCurrentLocation$(): Observable<LocationModel> {
    return this.store.select(fromLocations.getCurrentLocation)
  }

  getLocationTotal$(): Observable<number> {
    return this.store.select(fromLocations.getLocationTotal)
  }

  getUrls$(): Observable<Object[]> {
    return this.store.select(fromLocations.getLocationUrls)
  }

  getLocationError$(): Observable<string> {
    return this.store.select(fromLocations.getErrorLocations)
  }

  searchLocations(pages: { page: number, size: number }) {
    this.store.dispatch(LocationsActions.searchLocations({ pages }))
  }

  searchArchivedLocations() {
    this.store.dispatch(LocationsActions.searchArchivedLocations())
  }

  findLocationById(locationId: string) {
    this.store.dispatch(LocationsActions.findLocationById({ locationId }))
  }

  searchLocationByQuery(payload: { page: number, size: number, query: string }) {
    this.store.dispatch(LocationsActions.searchLocationByQuery(payload))
  }

  addLocation(location: LocationModel) {
    this.store.dispatch(LocationsActions.addLocation({ location }))
  }

  deleteLocation(locationId: string) {
    this.store.dispatch(LocationsActions.deleteLocation({ locationId }))
  }

  archiveLocation(locationId: string) {
    this.store.dispatch(LocationsActions.archiveLocation({ locationId }))
  }

  editLocation(location: LocationModel) {
    this.store.dispatch(LocationsActions.editLocation({ location }))
  }

  editLocationMaxQuota(location: LocationModel) {
    this.store.dispatch(LocationsActions.editLocationMaxQuota({ location }))
  }

  setCurrentLocation(location: LocationModel) {
    this.store.dispatch(LocationsActions.setCurrentLocation({ location }))
  }

  clearCurrentLocation() {
    this.store.dispatch(LocationsActions.clearCurrentLocation())
  }

  searchUrls(locationId: string) {
    this.store.dispatch(LocationsActions.getUrls({ locationId }))
  }

  filterContactsByStatus(payload: { page: number, size: number, filter: FilterContactsModel }) {
    const request: RequestModel = {
      params: `contacts/filter?page=${payload.page}&size=${payload.size}`,
      payload: payload.filter
    }
    this.store.dispatch(ContactsActions.filterContactsByStatus({ request }))
  }

  validateShortName(shortName) {
    const request: RequestModel = {
      params: `locations/short-name?shortName=${shortName}`,
    }
    this.store.dispatch(LocationsActions.validateShortName({ request }))
  }

  getExistShortName$(): Observable<boolean> {
    return this.store.select(fromLocations.getExistShortName)
  }

  getContactsLast14Days(locationId: string) {
    const request: RequestModel = {
      params: `locations/contactsDays?locationId=${locationId}`,
    }
    this.store.dispatch(LocationsActions.getContactsLast14Days({ request }))
  }

  getContactsLast14Days$(): Observable<number> {
    return this.store.select(fromLocations.getContactsLast14Days)
  }

  getReviewsLast14Days(locationId: string) {
    const request: RequestModel = {
      params: `locations/reviews-last-14-days?locationId=${locationId}`
    }
    this.store.dispatch(LocationsActions.getReviewsLast14Days({ request }))
  }

  getReviewsLast14Days$(): Observable<number> {
    return this.store.select(fromLocations.getReviewsLast14Days)
  }

  isSearchingLocations$(): Observable<boolean> {
    return this.store.select(fromLocations.isSearchingLocations);
  }

  isLocationCreated$(): Observable<boolean> {
    return this.store.select(fromLocations.isLocationCreated)
  }

  searchLocationByQueryAndLandingUrls(payload: { page: number, size: number, query: string }) {
    this.store.dispatch(LocationsActions.searchLocationByQueryAndLandingUrls(payload))
  }
  upgradePlan(payload: { action: UpgradeAction, cancelFeatureButton: string }) {
    this.store.dispatch(LocationsActions.upgradePlan({ payload }))
  }
  enableOrDisableAutoReplyAi(payload: { locationId: string, data: AiAutoReplyConfigModel }) {
    this.store.dispatch(LocationsActions.enableOrDisableAutoReplyAi({ payload }))
  }
  getEmployeeInfo$(payload: { page: number, size: number, locationId: string }): Observable<{ data: EmployeeResponse[], total: number }> {
    return this.http
      .get<{ data: EmployeeResponse[], total: number }>(`${environment.apiPath}/locations/employee?page=${payload.page}&size=${payload.size}&locationId=${payload.locationId}`)
  }
  createEmployee(payload: { locationId: string, firstName: string, lastName: string, campaignId: string, title: string }): Observable<EmployeeDTO> {
    const body = {
      ...payload,
      fullName: `${payload.firstName} ${payload.lastName}`.trim()
    }
    return this.http.post<EmployeeDTO>(`${environment.apiPath}/locations/employee`, body)
  }
  deleteEmployee(payload: { employeeId: string }): Observable<{ result: string }> {
    return this.http.post<{ result: string }>(`${environment.apiPath}/locations/employee/archive`, payload)
  }
  updateEmployee(payload: { employeeId: string, firstName: string, lastName: string, campaignId: string, title: string }): Observable<EmployeeDTO> {
    const body = {
      ...payload,
      fullName: `${payload.firstName} ${payload.lastName}`.trim(),
      id: payload.employeeId
    }
    return this.http.post<EmployeeDTO>(`${environment.apiPath}/locations/employee/update`, body)
  }
  getEmployeeInfoInterations$(payload: {
    page: number, 
    size: number,
    locationId: string,
    startDate: number,
    endDate: number 
  }): Observable< {name:string, count:number}[] > {
    return this.http
      .get<{name:string, count:number}[]>(`${environment.apiPath}/locations/employee/leaderboard-interactions?page=${payload.page}&size=${payload.size}&locationId=${payload.locationId}&startDate=${payload.startDate}&endDate=${payload.endDate}`)
  }

  getEmployeeInfoMentions$(payload: {
    page: number, 
    size: number,
    locationId: string,
    startDate: number,
    endDate: number 
  }): Observable< {name:string, count:number}[] > {
    return this.http
      .get<{name:string, count:number}[]>(`${environment.apiPath}/locations/employee/leaderboard-mentions?page=${payload.page}&size=${payload.size}&locationId=${payload.locationId}&startDate=${payload.startDate}&endDate=${payload.endDate}`)
  }
   getEmployeeInfoLeaderboard$(payload: {
    page: number, 
    size: number,
    locationId: string,
    startDate: number,
    endDate: number,
    eventType: string 
  }): Observable< {name:string, count:number}[] > {
    if (payload.eventType === 'INTERACTIONS') {
      return this.getEmployeeInfoInterations$(payload)
    } else if (payload.eventType === 'MENTIONS') {
      return this.getEmployeeInfoMentions$(payload)
    }
    return of([])
  }
  createLeaderboard(payload: { locationId: string,headerColor:string }): Observable<LocationModel> {
    const body = {
      locationId: payload.locationId,
      headerColor: payload.headerColor
    }
    return this.http.post<LocationModel>(`${environment.apiPath}/locations/generate-leaderboard-url`, body)
  }
  searchReviewRequestTotalLocation$(payload: {
    locationId?: string,
    startDate: number,
    endDate: number
  }): Observable<{ total: number }> {
    return this.http
      .get<{ total: number }>(`${environment.apiPath}/business-report/location/sent-review-requests-total?locationId=${payload.locationId}&startTime=${payload.startDate}&endTime=${payload.endDate}`)
  }
  searchLandingVisitTotalLocation$(payload: {
    locationId?: string,
    startDate: number,
    endDate: number
  }): Observable<{ total: number }> {
    return this.http
      .get<{ total: number }>(`${environment.apiPath}/business-report/location/landing-visit-total?locationId=${payload.locationId}&startTime=${payload.startDate}&endTime=${payload.endDate}`)
  }
  searchClickedOnReviewSiteTotalLocation$(payload: {
    locationId?: string,
    startDate: number,
    endDate: number
  }): Observable<{ total: number }> {
    return this.http
      .get<{ total: number }>(`${environment.apiPath}/business-report/location/clicked-on-review-site-total?locationId=${payload.locationId}&startTime=${payload.startDate}&endTime=${payload.endDate}`)
  }
  searchClickedOnFeedbackTotalLocation$(payload: {
    locationId?: string,
    startDate: number,
    endDate: number
  }): Observable<{ total: number }> {
    return this.http
      .get<{ total: number }>(`${environment.apiPath}/business-report/location/clicked-on-feedback-total?locationId=${payload.locationId}&startTime=${payload.startDate}&endTime=${payload.endDate}`)
  }
  searchClickedOnReviewSiteBreakDownTotalLocation$(payload: {
    locationId?: string,
    startDate: number,
    endDate: number
  }): Observable<[{ reviewSiteName: string, total: number }]> {
    return this.http
      .get<[{ reviewSiteName: string, total: number }]>(`${environment.apiPath}/business-report/location/clicked-by-review-site-total?locationId=${payload.locationId}&startTime=${payload.startDate}&endTime=${payload.endDate}`)
  }
  searchReviewCountFilterByClickOnReview$(payload: {
    locationId?: string,
    startDate: number,
    endDate: number
  }): Observable<{ total: number }> {
    return this.http
      .get<{ total: number }>(`${environment.apiPath}/business-report/location/review-count-filter-by-click-on-review?locationId=${payload.locationId}&startTime=${payload.startDate}&endTime=${payload.endDate}`)
  }
  getTotalReviewDirectContactLink$(payload: {
    locationId?: string,
    startDate: number,
    endDate: number
  }): Observable<{ total: number, lastValue: { reviewSiteName: string, total: number }[] }> {
    return this.http
      .get<{ total: number, lastValue: { reviewSiteName: string, total: number }[] }>(`${environment.apiPath}/business-report/location/review-count-direct-link?locationId=${payload.locationId}&startTime=${payload.startDate}&endTime=${payload.endDate}`)
  }
  getTotalVisitDirectContactLink$(payload: {
    locationId?: string,
    startDate: number,
    endDate: number
  }): Observable<{ total: number }> {
    return this.http
      .get<{ total: number }>(`${environment.apiPath}/business-report/location/review-mock-contact-count-visit?locationId=${payload.locationId}&startTime=${payload.startDate}&endTime=${payload.endDate}`)
  }

  findTaxonomyByLocationId(locationId: string): Observable<TaxonomyModel[]> {
    return this.http.get<TaxonomyModel[]>(`${environment.apiPath}/locations/taxonomy?locationId=${locationId}`)
  }
  findReviewTopInsights(locationId: string,startDate?:number,endDate?:number): Observable<{ strengths: InsightModel[], issues: InsightModel[] }> {
    return this.http.get<{ strengths: InsightModel[], issues: InsightModel[] }>(`${environment.apiPath}/locations/review-top-insight?locationId=${locationId}&startDate=${startDate}&endDate=${endDate}`)
  }
  generateTaxonomy(locationId: string): Observable<TaxonomyModel[]> {
    return this.http.post<TaxonomyModel[]>(`${environment.apiPath}/locations/generate-taxonomy?locationId=${locationId}&reviewSamples=150`, {})
  }
  generateClassification(locationId: string): Observable<{result: string}> {
    return this.http.post<{result: string}>(`${environment.apiPath}/locations/generate-review-analysis?locationId=${locationId}`, {})
  }

  findReviewAnalysis(locationId: string,startDate?:number,endDate?:number): Observable<LabelAggregationModel[]> {
    return this.http.get<LabelAggregationModel[]>(`${environment.apiPath}/locations/review-insight?locationId=${locationId}&startDate=${startDate}&endDate=${endDate}`)
      .pipe(
        map(response => {
          // sort by total mentions descending
          return response.sort((a, b) => b.total - a.total);  
        }
      ))
  }
}